import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useObsCostByUser, useObsAdoptionByUser } from '@/hooks/useObservability';
import { LoaderBlock, fmtUSD, fmtNum } from './shared';

export function ObsUserTab() {
  const { t } = useTranslation();
  const { data: costRows = [], isLoading } = useObsCostByUser();
  const { data: adoptRows = [] } = useObsAdoptionByUser();
  const [search, setSearch] = useState('');

  const summary = useMemo(() => {
    const map = new Map<string, {
      email: string; area: string | null; usd: number;
      gemini_chat: number; gemini_ws: number; cc: number; mcp: number; vendors: Set<string>;
    }>();
    for (const r of costRows) {
      const cur = map.get(r.user_email) || {
        email: r.user_email, area: r.area, usd: 0,
        gemini_chat: 0, gemini_ws: 0, cc: 0, mcp: 0, vendors: new Set<string>(),
      };
      cur.usd += r.ai_cost_usd || 0;
      cur.area = cur.area || r.area;
      (r.sample_vendors || '').split(',').forEach((v) => v && cur.vendors.add(v.trim()));
      map.set(r.user_email, cur);
    }
    for (const r of adoptRows) {
      const cur = map.get(r.user_email);
      if (!cur) continue;
      if (r.source === 'gemini_chat') cur.gemini_chat += r.events;
      if (r.source === 'gemini_workspace') cur.gemini_ws += r.events;
      if (r.source === 'claude_code') cur.cc += r.events;
      if (r.source === 'mcp_agentic') cur.mcp += r.events;
    }
    return Array.from(map.values()).sort((a, b) => b.usd - a.usd);
  }, [costRows, adoptRows]);

  const filtered = summary.filter(
    (r) => !search ||
           r.email.toLowerCase().includes(search.toLowerCase()) ||
           (r.area || '').toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) return <LoaderBlock />;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold">{t('observability.user.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('observability.user.subtitle')}</p>
        </div>
        <Input placeholder={t('observability.user.search_placeholder')} value={search}
               onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3 w-8">{t('observability.user.col_rank')}</th>
              <th className="pr-3">{t('observability.user.col_user')}</th>
              <th className="pr-3">{t('observability.user.col_area')}</th>
              <th className="pr-3 text-right">{t('observability.user.col_usd')}</th>
              <th className="pr-3 text-right">{t('observability.user.col_gem_chat')}</th>
              <th className="pr-3 text-right">{t('observability.user.col_gem_ws')}</th>
              <th className="pr-3 text-right">{t('observability.user.col_cc_sessions')}</th>
              <th className="pr-3 text-right">{t('observability.user.col_mcp_execs')}</th>
              <th>{t('observability.user.col_vendors')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 100).map((r, i) => (
              <tr key={r.email} className="border-b hover:bg-muted/50">
                <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                <td className="pr-3 text-xs">{r.email}</td>
                <td className="pr-3 text-xs">{r.area || '—'}</td>
                <td className="pr-3 text-right font-mono font-semibold">{fmtUSD(r.usd)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.gemini_chat)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.gemini_ws)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.cc)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.mcp)}</td>
                <td className="text-xs text-muted-foreground truncate max-w-[180px]">
                  {Array.from(r.vendors).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        {t('observability.user.showing_users', { shown: Math.min(filtered.length, 100), total: filtered.length })}
      </div>
    </Card>
  );
}
